<script setup lang="ts">
/**
 * Select com busca — pesquisa local entre opções ou remota via fetchFn.
 *
 * @remarks
 * Adaptado do design system Fluent 2. Exibe até 3 itens visíveis com
 * overflow-y para scroll. Emite `option-selected` sempre que uma opção
 * é escolhida, permitindo que telas consumidoras atualizem previews.
 *
 * @example
 * ```vue
 * <SearchSelect
 *   v-model="selectedId"
 *   :options="templateOptions"
 *   placeholder="Nenhuma"
 *   @option-selected="onTemplatePicked"
 * />
 * ```
 */
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";

export interface SearchSelectOption {
  id: number | string;
  label: string;
  sub?: string;
}

const props = withDefaults(
  defineProps<{
    modelValue: number | string | null;
    placeholder?: string;
    options?: SearchSelectOption[];
    fetchFn?: (search: string) => Promise<SearchSelectOption[]>;
    disabled?: boolean;
  }>(),
  {
    placeholder: "Selecionar...",
    disabled: false,
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: number | string | null];
  "option-selected": [option: SearchSelectOption];
}>();

const isOpen = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
const containerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const inputText = ref("");
const results = ref<SearchSelectOption[]>([]);
const loading = ref(false);
const dropdownStyle = ref({ top: "0px", left: "0px", width: "0px", maxHeight: "120px" });

const selectedOption = computed(() =>
  props.options?.find((o) => o.id === props.modelValue) ?? null
);

watch(
  () => props.modelValue,
  () => {
    if (!isOpen.value) syncInputToSelected();
  }
);

function syncInputToSelected(): void {
  if (props.modelValue === null || props.modelValue === "" || props.modelValue === "none") {
    inputText.value = "";
  } else if (selectedOption.value) {
    inputText.value = selectedOption.value.label;
  }
}

onMounted(syncInputToSelected);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

async function loadResults(search: string): Promise<void> {
  if (props.options) {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? props.options.filter(
          (o) =>
            o.label.toLowerCase().includes(q) ||
            o.sub?.toLowerCase().includes(q)
        )
      : props.options;
    results.value = filtered.slice(0, 50);
    return;
  }
  if (props.fetchFn) {
    loading.value = true;
    try {
      const data = await props.fetchFn(search);
      results.value = data.slice(0, 50);
    } catch {
      results.value = [];
    } finally {
      loading.value = false;
    }
  }
}

/**
 * Recalcula a posição e altura máxima do dropdown com base no viewport visual.
 *
 * @remarks
 * No mobile, o teclado virtual reduz a altura do visualViewport. Esta função
 * usa `window.visualViewport.height` quando disponível para garantir que o
 * dropdown nunca fique escondido atrás do teclado. Se não houver espaço
 * suficiente abaixo do input, o dropdown abre para cima.
 */
function updateDropdownPosition(): void {
  if (!containerRef.value) return;
  const rect = containerRef.value.getBoundingClientRect();
  const gap = 4;

  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const maxDropdownHeight = 130;
  const spaceBelow = viewportHeight - rect.bottom - gap;

  let top: number;
  let maxH: number;

  if (spaceBelow >= maxDropdownHeight || spaceBelow >= rect.top) {
    top = rect.bottom + gap;
    maxH = Math.min(maxDropdownHeight, spaceBelow - 8);
  } else {
    const spaceAbove = rect.top - gap;
    top = rect.top - Math.min(maxDropdownHeight, spaceAbove) - gap;
    maxH = Math.min(maxDropdownHeight, spaceAbove - 8);
  }

  if (top < 8) {
    maxH = maxH + top - 8;
    top = 8;
  }

  dropdownStyle.value = {
    top: `${top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    maxHeight: `${Math.max(maxH, 40)}px`,
  };
}

/** Handler para eventos de resize/scroll do visualViewport (teclado mobile). */
function onVisualViewportChange(): void {
  if (isOpen.value) updateDropdownPosition();
}

async function openDropdown(): Promise<void> {
  if (props.disabled || isOpen.value) return;
  updateDropdownPosition();
  isOpen.value = true;
  inputText.value = "";
  await loadResults("");
  await nextTick();
  inputRef.value?.focus();
  inputRef.value?.select();
}

function closeDropdown(): void {
  isOpen.value = false;
  syncInputToSelected();
  if (debounceTimer) clearTimeout(debounceTimer);
}

function onInputClick(): void {
  if (!isOpen.value) openDropdown();
}

function onInputFocus(): void {
  if (!isOpen.value) openDropdown();
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === "Escape") closeDropdown();
}

watch(inputText, (val) => {
  if (!isOpen.value) return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => loadResults(val), 200);
});

function select(option: SearchSelectOption): void {
  emit("update:modelValue", option.id);
  emit("option-selected", option);
  inputText.value = option.label;
  isOpen.value = false;
}

function clearValue(e: MouseEvent | TouchEvent): void {
  e.stopPropagation();
  emit("update:modelValue", null);
  inputText.value = "";
  results.value = [];
  isOpen.value = false;
}

function onClickOutside(e: MouseEvent | TouchEvent): void {
  const target = e.target as Node;
  if (
    containerRef.value?.contains(target) ||
    dropdownRef.value?.contains(target)
  )
    return;
  closeDropdown();
}

onMounted(() => {
  document.addEventListener("mousedown", onClickOutside);
  document.addEventListener("touchstart", onClickOutside, { passive: true });
  window.visualViewport?.addEventListener("resize", onVisualViewportChange);
  window.visualViewport?.addEventListener("scroll", onVisualViewportChange);
});
onUnmounted(() => {
  document.removeEventListener("mousedown", onClickOutside);
  document.removeEventListener("touchstart", onClickOutside);
  window.visualViewport?.removeEventListener("resize", onVisualViewportChange);
  window.visualViewport?.removeEventListener("scroll", onVisualViewportChange);
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>

<template>
  <div
    ref="containerRef"
    class="search-select"
    v-bind:class="{ 'search-select--disabled': disabled }"
    v-on:keydown="onKeydown"
  >
    <div class="search-select__input-wrap">
      <input
        ref="inputRef"
        v-model="inputText"
        type="text"
        v-bind:placeholder="placeholder"
        v-bind:disabled="disabled"
        class="search-select__input"
        autocomplete="off"
        v-on:click="onInputClick"
        v-on:focus="onInputFocus"
      />
      <button
        v-if="modelValue !== null && modelValue !== '' && modelValue !== 'none'"
        type="button"
        class="search-select__clear"
        tabindex="-1"
        aria-label="Limpar seleção"
        v-on:mousedown.prevent="clearValue"
        v-on:touchstart.prevent="clearValue"
      >
        <v-icon name="md-close" />
      </button>
      <span
        class="search-select__chevron"
        v-bind:class="{ 'search-select__chevron--open': isOpen }"
        aria-hidden="true"
      >
        <v-icon name="md-arrowdropdown-outlined" />
      </span>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="search-select__dropdown"
      v-bind:style="dropdownStyle"
    >
      <div
        v-if="loading"
        class="search-select__dropdown-status"
      >
        <span class="search-select__spinner" aria-hidden="true"></span>
        Buscando...
      </div>

      <template v-else-if="results.length > 0">
        <button
          v-for="opt in results"
          v-bind:key="opt.id"
          type="button"
          class="search-select__option"
          v-bind:class="{ 'search-select__option--selected': opt.id === modelValue }"
          v-on:mousedown.prevent="select(opt)"
          v-on:touchstart.prevent="select(opt)"
        >
          <span class="search-select__option-label">{{ opt.label }}</span>
          <span
            v-if="opt.sub"
            class="search-select__option-sub"
          >{{ opt.sub }}</span>
        </button>
      </template>

      <div
        v-else
        class="search-select__dropdown-status"
      >
        Nenhum resultado
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.search-select {
  width: 100%;
  position: relative;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.search-select__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.search-select__input {
  width: 100%;
  padding: var(--spacing-sm) 12px;
  padding-right: 52px;
  font-size: var(--font-size-md);
  font-family: var(--font-family);
  color: var(--colorText);
  background-color: var(--colorBackground);
  border: 1px solid var(--colorBorder);
  border-radius: var(--radius-sm);
  outline: none;
  cursor: pointer;
  transition: border-color var(--transition), box-shadow var(--transition);

  &::placeholder {
    color: var(--colorTextSecondary);
  }

  &:focus {
    border-color: var(--colorBorderFocus);
    box-shadow: 0 0 0 1px var(--colorBorderFocus);
    cursor: text;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.search-select__clear {
  position: absolute;
  right: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--colorTextSecondary);
  cursor: pointer;
  padding: 0;

  .ov-icon {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: var(--colorBackgroundHover);
    color: var(--colorText);
  }
}

.search-select__chevron {
  position: absolute;
  right: 8px;
  display: inline-flex;
  align-items: center;
  color: var(--colorTextSecondary);
  pointer-events: none;
  transition: transform var(--transition);

  .ov-icon {
    width: 16px;
    height: 16px;
  }

  &--open {
    transform: rotate(180deg);
  }
}

.search-select__dropdown {
  position: fixed;
  z-index: 9999;
  background: var(--colorBackground);
  border: 1px solid var(--colorBorder);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow8);
  overflow-y: auto;
  padding: var(--spacing-xs);
}

.search-select__option {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: left;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  color: var(--colorText);
  line-height: 1.4;
  transition: background var(--transition);

  &:hover {
    background: var(--colorBackgroundHover);
  }

  &--selected {
    background: var(--colorBackgroundSubtle);
    font-weight: 600;

    .search-select__option-label {
      color: var(--colorPrimary);
    }
  }
}

.search-select__option-sub {
  font-size: 11px;
  color: var(--colorTextTertiary);
  margin-top: 1px;
}

.search-select__dropdown-status {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
  color: var(--colorTextSecondary);
}

.search-select__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid var(--colorBorder);
  border-top-color: var(--colorPrimary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>